import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  HeartPulse, ShieldCheck, MessageCircleHeart, BookHeart, Building2, Wind,
  FileText, FilePieChart, FileSignature, ArrowRight, CheckCircle2, Globe2,
  Smartphone, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForNhs() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", role: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ── Per-route SEO ──
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "SafeHaven · Digital Safety Plan for Hospitals & Communities";
    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", name); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    };
    const setOg = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("property", property); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    };
    const desc = "SafeHaven is a digital safety plan for hospitals and communities — used across NHS Trusts, private hospitals, supported living and community mental-health services to promote safety, recovery and wellbeing. Evidence-based, UK-GDPR compliant, multi-language.";
    setMeta("description", desc);
    setMeta("keywords", "digital safety plan, safety planning intervention, NHS safety planning, private mental health, suicide prevention, hospital mental health app, community mental health, WRAP, CQC mental health, supported living");
    setOg("og:title", "SafeHaven · Digital Safety Plan for Hospitals & Communities");
    setOg("og:description", desc);
    setOg("og:type", "website");
    setOg("og:url", "https://www.safehavensafetyplan.org/for-nhs");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://www.safehavensafetyplan.org/for-nhs");

    return () => { document.title = prevTitle; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organisation || !form.role) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/nhs`, form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within two working days.");
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not send — please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="for-nhs-page">
      {/* ── Top nav ── */}
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/for-nhs" className="flex items-center gap-2 font-display font-bold text-lg" data-testid="for-nhs-logo">
            <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-teal-700 text-white"><HeartPulse className="w-5 h-5" /></span>
            SafeHaven
          </Link>          <div className="flex items-center gap-2">
            <a href="#book" className="hidden sm:inline-block text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Book a demo</a>
            <a href="#downloads" className="hidden sm:inline-block text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Download brief</a>
            <Link to="/login" data-testid="for-nhs-signin" className="text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2">
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-teal-50 to-transparent" />
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-12 lg:pt-24 lg:pb-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 text-xs font-semibold px-3 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Evidence-based · Clinically built · UK GDPR Art. 28
            </div>
            <FounderMiniPill />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05]">
              A digital safety plan <span className="text-teal-700">for hospitals and communities</span> — promoting safety, recovery and wellbeing.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl">
              SafeHaven turns paper safety plans — which get lost, are illegible at A&amp;E, and never travel with the patient —
              into a calm digital companion that stays with people between crises, alerts clinicians on deterioration,
              and gives services anonymised quarterly evidence for governance and inspection.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#book" data-testid="hero-book-demo" className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2">
                Book a 15-minute demo <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#downloads" data-testid="hero-download-brief" className="bg-white border border-slate-300 hover:bg-slate-100 font-semibold rounded-full px-6 py-3">
                Download the brief
              </a>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="4" label="Languages" />
              <Stat n="7-step" label="Safety plan" />
              <Stat n="12-week" label="Service trend chart" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-slate-200 shadow-xl p-6">
              <div className="flex items-center gap-2 text-xs font-semibold text-teal-800 uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4" /> Patient view · distress = 8
              </div>
              <div className="mt-3 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 text-white p-5">
                <div className="text-sm opacity-90">Step 4 · Trusted support</div>
                <div className="font-display text-2xl font-bold mt-1">Call Lucas</div>
                <div className="text-xs opacity-80 mt-1">Son · primary reason for living</div>
                <button className="mt-4 bg-white text-teal-900 font-semibold rounded-full px-4 py-2 text-sm">Call now</button>
              </div>
              <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                <b>AI Companion:</b> &ldquo;You previously identified Lucas as your most important reason for staying safe. Let&apos;s work through your safety plan together.&rdquo;
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Pill>5-4-3-2-1 grounding</Pill>
                <Pill>Box breathing</Pill>
                <Pill>Photos of Lucas</Pill>
                <Pill>NHS 111</Pill>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Problem / Approach / Why now ── */}
      <section className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-3 gap-6">
        <Card tone="rose" title="The problem" body="Paper safety plans are lost, illegible at A&E, never updated, and absent from the patient record. Crisis teams duplicate work, families are excluded, and clinicians lack outcome evidence." />
        <Card tone="teal" title="Our approach" body="A calm, accessible digital safety plan built around the evidence-based 7-step intervention — with WRAP, an AI Safety-Plan Companion, trusted-contact one-tap, environment safety checklists, and a Personal Safety Vault that surfaces reasons-for-living when distress is highest." />
        <Card tone="amber" title="Why now" body="Aligned with the NHS Long Term Plan, the Suicide Prevention Strategy 2023–2028, CQC expectations, and the equivalent governance standards in private hospitals and supported-living providers. Available today, deployable in days." />
      </section>

      {/* ── Feature grid ── */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Built around the clinical intervention, not bolted on.</h2>
          <p className="text-slate-600 mt-3 max-w-3xl">Every feature traces back to either an evidence-based clinical intervention, the Wellness Recovery Action Plan, or recognised information-governance requirements. No generic wellness fluff.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            <Feature icon={ShieldCheck} title="7-step safety plan" body="Warning signs, internal coping, social distraction, trusted support, professional support, emergency support, environment safety — based on the Stanley &amp; Brown intervention." />
            <Feature icon={MessageCircleHeart} title="AI Safety-Plan Companion" body="Carefully bounded AI (Claude Sonnet 4.6). Never replaces clinicians. Always escalates to UK crisis lines when distress is high." />
            <Feature icon={BookHeart} title="WRAP module" body="Mary Ellen Copeland&apos;s Wellness Recovery Action Plan: well-self, triggers, early warning signs, when-things-break-down, crisis, post-crisis." />
            <Feature icon={Building2} title="Multi-tenant service portal" body="Invite codes per organisation — NHS Trust, private hospital, supported-living provider. Anonymised 12-week trend chart of active users, check-ins and deterioration alerts." />
            <Feature icon={FilePieChart} title="Quarterly Impact Report" body="Branded PDF designed for inspection evidence (CQC, equivalent private regulators) and contract-renewal governance. Generated on demand or scheduled." />
            <Feature icon={Lock} title="DPA / DPIA in-product" body="UK GDPR Article 28 contract signable in-product by your Caldicott Guardian or information-governance lead. Full audit trail." />
            <Feature icon={Globe2} title="i18n: English · Welsh · Polish · Urdu" body="Equitable access for diverse hospital and community populations. Auto-translation of AI assistant replies." />
            <Feature icon={Smartphone} title="Web · iOS · Android" body="Native push notifications on iOS and Android via Expo; web push on desktop. Branded for SafeHaven out of the box." />
            <Feature icon={Wind} title="Crisis-time UX" body="One-tap call/SMS to trusted contacts. Hard-coded UK crisis-line directory (Samaritans, SHOUT, Papyrus, CALM, NHS 111, 999)." />
          </div>
        </div>
      </section>

      {/* ── Founder ── */}
      <section id="for-nhs-founder" className="bg-white border-y border-slate-200" data-testid="for-nhs-founder">
        <div className="max-w-6xl mx-auto px-5 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <FounderPortrait />
            <div className="mt-5">
              <div className="font-display text-xl font-bold text-slate-900">Ezekwesirim &ldquo;Eze&rdquo; Utchay</div>
              <div className="text-sm text-teal-800 font-semibold mt-1">RMN · MBA · PhD candidate (Lancaster)</div>
              <div className="text-sm text-slate-600 mt-3 space-y-0.5">
                <div>Registered Mental Health Nurse</div>
                <div>Director, Three Spheres Healthcare Ltd</div>
                <div>NMC PIN <span className="font-mono">21A1101E</span></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-3">About the founder</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
              SafeHaven was built by someone who has spent a decade inside the system it is designed to support.
            </h2>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                I qualified as a Registered Mental Health Nurse from the University of Gloucestershire in 2021, but my
                route into mental health was not a conventional one. I trained originally in international law and
                diplomacy in Nigeria, moved to the United Kingdom, and worked for six years as a Mental Health Support
                Worker across PICU, forensic, CAMHS, older-adult and dementia services. I watched, from the bedside,
                what does and does not happen when a person is at their most distressed. I watched safety plans being
                written on photocopied A4 in twenty rushed minutes before discharge. I watched those same plans
                disappear — into a folder, a ward office, a community team&apos;s filing cabinet — and almost never
                re-emerge at the point of crisis.
              </p>
              <p>
                Since qualifying I have practised across Avon and Wiltshire Mental Health Partnership NHS Trust and, in
                agency capacity, across multiple NHS Trusts on acute wards, PICU and general hospitals — the front-line
                settings where the Stanley and Brown Safety Planning Intervention is meant to do its work. In 2022 I
                founded <b>Three Spheres Healthcare Limited</b>, in 2025 I completed an MBA in Healthcare Management at
                Wrexham University, and I am currently a PhD candidate in Mental Health at Lancaster University,
                researching safety-planning fidelity and patient experience.
              </p>
              <p>
                SafeHaven exists because the gap between the evidence base — Stanley and Brown&apos;s intervention reduces
                suicidal behaviour by roughly 45% in published cohorts — and the lived reality on a Tuesday afternoon
                ward round is enormous. Closing that gap is not a software problem. It is a clinical-workflow problem
                with a software-shaped answer.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Acute & PICU", "Forensic", "CAMHS", "Older adult", "Community MH", "Crisis intervention"].map((t) => (
                <span key={t} className="text-xs font-semibold bg-teal-50 text-teal-800 px-3 py-1 rounded-full border border-teal-100">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Downloads ── */}
      <section id="downloads" className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold">Documents your procurement &amp; IG team will ask for.</h2>
        <p className="text-slate-600 mt-3 max-w-3xl">No form required — these are exactly what you&apos;d attach to a procurement review.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          <DownloadCard testId="public-pitch" href="/api/public/pitch-sheet.pdf" icon={FileText} title="One-page brief" desc="Problem · approach · feature grid · NHS readiness · pricing." />
          <DownloadCard testId="public-sample-ir" href="/api/public/sample-impact-report.pdf" icon={FilePieChart} title="Sample Impact Report" desc="A populated example of the quarterly Impact Report your Trust would receive." />
          <DownloadCard testId="public-dpa" href="/api/public/dpa-summary.pdf" icon={FileSignature} title="DPA &amp; DPIA summary" desc="Art. 28 UK GDPR contract and DPIA procurement summary." />
        </div>
      </section>

      {/* ── Lead form ── */}
      <section id="book" className="bg-gradient-to-br from-slate-900 to-teal-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Book a 15-minute demo.</h2>
            <p className="text-slate-300 mt-3 max-w-md">A focused fifteen minutes with a clinically-aware product team. We&apos;ll walk through the patient view, the Trust portal, the quarterly Impact Report, and the DPA signing flow. No slide deck.</p>
            <ul className="mt-8 space-y-3 text-slate-300">
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-teal-300 mt-0.5 shrink-0" /> Suitable for Directors of Mental Health, CCIOs, Caldicott Guardians, and Suicide-Prevention Leads.</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-teal-300 mt-0.5 shrink-0" /> We respond within two working days.</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-teal-300 mt-0.5 shrink-0" /> No commitment — this is a discovery conversation.</li>
            </ul>
          </div>
          <div>
            {submitted ? (
              <div className="rounded-3xl bg-white text-slate-900 p-8 shadow-2xl" data-testid="for-nhs-thanks">
                <CheckCircle2 className="w-10 h-10 text-teal-700" />
                <h3 className="font-display text-2xl font-bold mt-4">Thank you, {form.name.split(" ")[0]}.</h3>
                <p className="text-slate-600 mt-2">We&apos;ve received your enquiry about <b>{form.organisation}</b> and will be in touch within two working days. A confirmation has been sent to <b>{form.email}</b>.</p>
                <p className="text-slate-500 text-sm mt-6">In the meantime, the three PDFs above are immediately downloadable.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl bg-white text-slate-900 p-6 sm:p-8 shadow-2xl space-y-3" data-testid="for-nhs-form">
                <Input testId="lead-name" label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Dr Jane Smith" required />
                <Input testId="lead-email" label="Work email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jane.smith@your-hospital.org" required />
                <Input testId="lead-org" label="Hospital / organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} placeholder="e.g. South London and Maudsley NHS Trust, The Priory, Cygnet Health Care" required />
                <Input testId="lead-role" label="Your role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="Director of Mental Health · Clinical Lead · Ward Manager" required />
                <div>
                  <label className="text-xs font-semibold text-slate-600">What would you like to discuss? <span className="text-slate-400 font-normal">(optional)</span></label>
                  <textarea data-testid="lead-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                {/* honeypot — hidden from humans */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                  <label>Website</label>
                  <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </div>
                <button data-testid="lead-submit" type="submit" disabled={submitting}
                        className="w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-3 mt-2">
                  {submitting ? "Sending…" : "Request a 15-minute demo"}
                </button>
                <p className="text-xs text-slate-500 pt-1">We never share your details. By submitting you consent to one follow-up contact under UK GDPR legitimate interest.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-950 text-slate-400 text-sm">
        <div className="max-w-6xl mx-auto px-5 py-10 grid sm:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2 text-white font-display font-bold">
              <span className="inline-flex w-7 h-7 items-center justify-center rounded-md bg-teal-700"><HeartPulse className="w-4 h-4" /></span>
              SafeHaven
            </div>
            <p className="mt-3">Three Spheres Healthcare · safehaven@threespheres.healthcare</p>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Product</div>
            <ul className="space-y-1">
              <li><a href="#downloads" className="hover:text-white">Documents</a></li>
              <li><a href="#book" className="hover:text-white">Book a demo</a></li>
              <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
              <li><Link to="/register" className="hover:text-white">Create an account</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Clinical foundations</div>
            <ul className="space-y-1 text-xs">
              <li>Stanley B. &amp; Brown G.K. (2012). Safety Planning Intervention.</li>
              <li>Copeland M.E. WRAP®.</li>
              <li>NHS Long Term Plan · CQC Mental Health.</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 py-4 text-xs text-center">© {new Date().getFullYear()} Three Spheres Healthcare · SafeHaven · UK GDPR · ICO registered</div>
      </footer>
    </div>
  );
}

const FounderMiniPill = () => {
  const [failed, setFailed] = React.useState(false);
  return (
    <a href="#for-nhs-founder" data-testid="founder-mini-pill" className="mt-3 inline-flex items-center gap-3 bg-white border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all rounded-full pl-1 pr-4 py-1">
      <span className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-teal-100 shrink-0 bg-gradient-to-br from-teal-700 to-slate-900 flex items-center justify-center">
        {!failed ? (
          <img src="/founder.jpg" alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} />
        ) : (
          <span className="text-white font-display font-bold text-xs">EU</span>
        )}
      </span>
      <span className="text-xs text-slate-700">
        Built by an NHS nurse · <span className="font-semibold text-slate-900">Eze Utchay, RMN</span>
      </span>
    </a>
  );
};

const FounderPortrait = () => {
  const [imgFailed, setImgFailed] = React.useState(false);
  return (
    <div className="relative aspect-square w-full max-w-xs mx-auto lg:mx-0 rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200" data-testid="founder-portrait">
      {!imgFailed ? (
        <img
          src="/founder.jpg"
          alt="Eze Utchay, founder of Three Spheres Healthcare"
          className="w-full h-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="font-display text-7xl font-bold tracking-tight">EU</div>
            <div className="text-xs uppercase tracking-[0.2em] mt-3 text-teal-200">Founder</div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display text-2xl font-bold text-slate-900">{n}</div>
    <div className="text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: label }} />
  </div>
);

const Pill = ({ children }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700">{children}</div>
);

const Card = ({ tone, title, body }) => {
  const palettes = {
    rose: "bg-rose-50 border-rose-100",
    teal: "bg-teal-50 border-teal-100",
    amber: "bg-amber-50 border-amber-100",
  };
  return (
    <div className={`rounded-2xl border p-6 ${palettes[tone] || palettes.teal}`}>
      <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-700 mt-2 leading-relaxed">{body}</p>
    </div>
  );
};

const Feature = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-slate-200 p-5 hover:border-teal-300 transition-colors">
    <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
    <div className="font-display font-bold mt-3">{title}</div>
    <div className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</div>
  </div>
);

const DownloadCard = ({ testId, href, icon: Icon, title, desc }) => (
  <a data-testid={testId} href={href} target="_blank" rel="noopener noreferrer"
     className="block rounded-2xl border border-slate-200 bg-white p-6 hover:border-teal-300 hover:shadow-md transition-all">
    <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
    <div className="font-display font-bold mt-4" dangerouslySetInnerHTML={{ __html: title }} />
    <div className="text-sm text-slate-600 mt-1">{desc}</div>
    <div className="text-sm font-semibold text-teal-700 mt-3 inline-flex items-center gap-1">Open PDF <ArrowRight className="w-4 h-4" /></div>
  </a>
);

const Input = ({ testId, label, value, onChange, type = "text", placeholder = "", required = false }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-rose-600"> *</span>}</label>
    <input data-testid={testId} type={type} value={value} required={required} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)}
           className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
  </div>
);
